import { Outlet, matchPath, useLocation } from 'react-router';

import { PAGE_CONTAINER_ID } from 'constants/layout';

import { Wrapper, cn } from 'components';
import { Subdirectory } from 'constants/routing';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Footer } from './Footer';
import { Header } from './Header';
import { NAV_BAR_HEIGHT_PX, NAV_BAR_TRANSITION, NavBar } from './NavBar';
import ScrollToTop from './ScrollToTop';
import { TestEnvWarning, isTestEnvWarningVisible } from './TestEnvWarning';
import './lightTheme.css';
import { type NavRevealState, store } from './store';
import { themeStore } from './themeStore';

const NO_WRAPPER_PATHNAMES = [Subdirectory.LANDING] as string[];

// Cumulative scroll (px) in one direction before the nav reacts — filters out
// momentum jitter and trackpad noise
const NAV_DIRECTION_HYSTERESIS_PX = 12;
// Above this offset, scrolling down hides the nav
const NAV_HIDE_THRESHOLD_PX = 120;
// Below this offset, the nav always shows its integrated "top" appearance
const NAV_TOP_THRESHOLD_PX = 40;

interface ResolveNavRevealStateInput {
  accumulatedDelta: number;
  currentState: NavRevealState;
  delta: number;
  scrollTop: number;
}

export const resolveNavRevealState = ({
  accumulatedDelta,
  currentState,
  delta,
  scrollTop,
}: ResolveNavRevealStateInput): {
  accumulatedDelta: number;
  state: NavRevealState;
} => {
  if (scrollTop < NAV_TOP_THRESHOLD_PX) {
    return { accumulatedDelta: 0, state: 'top' };
  }

  const nextAccumulatedDelta = delta * accumulatedDelta < 0 ? delta : accumulatedDelta + delta;

  if (nextAccumulatedDelta > NAV_DIRECTION_HYSTERESIS_PX && scrollTop > NAV_HIDE_THRESHOLD_PX) {
    return { accumulatedDelta: nextAccumulatedDelta, state: 'hidden' };
  }

  if (nextAccumulatedDelta < -NAV_DIRECTION_HYSTERESIS_PX) {
    return { accumulatedDelta: nextAccumulatedDelta, state: 'revealed' };
  }

  return { accumulatedDelta: nextAccumulatedDelta, state: currentState };
};

export const Layout: React.FC = () => {
  const scrollToTopRef = useRef<HTMLButtonElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  const scrollDeltaRef = useRef(0);
  const [testWarningHeight, setTestWarningHeight] = useState(0);

  const { pathname } = useLocation();

  const isCloseToBottomState = store.use.isCloseToBottom();
  const setIsCloseToBottom = store.use.setIsCloseToBottom();
  const setScrollToTopVisible = store.use.setScrollToTopVisible();
  const isScrollToTopVisible = store.use.isScrollToTopVisible();
  const navRevealState = store.use.navRevealState();
  const setNavRevealState = store.use.setNavRevealState();
  const shouldReduceMotion = useReducedMotion();
  const viewportHeight = window.innerHeight;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const scrollElem = event.currentTarget;
    const shouldShowScrollToTopVisible =
      scrollElem?.scrollTop && scrollElem.scrollTop > viewportHeight;

    // Scroll-aware nav: integrated at the top, hides scrolling down, reveals
    // as a compact overlay bar scrolling up. Clamp guards iOS rubber-banding.
    const scrollTop = Math.max(0, scrollElem.scrollTop);
    const delta = scrollTop - lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    const resolvedNavState = resolveNavRevealState({
      accumulatedDelta: scrollDeltaRef.current,
      currentState: navRevealState,
      delta,
      scrollTop,
    });
    const nextNavRevealState = resolvedNavState.state;
    scrollDeltaRef.current = resolvedNavState.accumulatedDelta;

    if (nextNavRevealState !== navRevealState) {
      setNavRevealState(nextNavRevealState);
    }

    const scrollToTopRect = scrollToTopRef.current?.getBoundingClientRect();
    const footerRect = footerRef.current?.getBoundingClientRect();
    const footerTopPos = footerRect ? footerRect.top - footerRect.height : undefined;

    // if the state is already flagged, it means we the scroll pos has already gone up
    // which in turn will make the bottom position move up
    // causing the next values of `isCloseToBottom` to switch between true and false until they settle
    // we treat this case by calculating a reference position
    const scrollBtnBottom = scrollToTopRect?.bottom ?? 0;
    const scrollBtnHeight = scrollToTopRect?.height ?? 0;
    // 1.25 is not arbitrary, it's related to the the styling of ScrollToTop
    // it's currently moving up by 125% of its height
    const scrollBtnDisplacement = isCloseToBottomState ? scrollBtnHeight * 1.25 : 0;
    const actualBottomReference = scrollBtnBottom + scrollBtnDisplacement;
    const isCloseToBottom = footerTopPos ? actualBottomReference >= footerTopPos : false;

    if (isCloseToBottomState !== isCloseToBottom) {
      setIsCloseToBottom(isCloseToBottom);
    }

    if (shouldShowScrollToTopVisible && !isScrollToTopVisible) {
      setScrollToTopVisible(true);
    } else if (!shouldShowScrollToTopVisible && isScrollToTopVisible) {
      setScrollToTopVisible(false);
    }
  };

  const noWrapper = NO_WRAPPER_PATHNAMES.some((noWrapperPath) =>
    matchPath(noWrapperPath, pathname),
  );
  const theme = themeStore.use.theme();

  useEffect(() => {
    document.documentElement.dataset.forgeTheme = theme;

    return () => {
      delete document.documentElement.dataset.forgeTheme;
    };
  }, [theme]);

  // Route changes start a fresh page context. Do not carry a hidden/revealed
  // nav state or accumulated scroll momentum into the destination route.
  useEffect(() => {
    lastScrollTopRef.current = 0;
    scrollDeltaRef.current = 0;
    setNavRevealState('top');
  }, [pathname, setNavRevealState]);

  const WrapperComp = noWrapper ? 'div' : Wrapper;
  const contentDom = (
    <>
      <Outlet />
      <ScrollToTop ref={scrollToTopRef} />
    </>
  );

  return (
    <div
      className={cn('h-dvh flex flex-col relative', `forge-${theme}-shell`)}
      style={{ '--forge-test-warning-height': `${testWarningHeight}px` } as React.CSSProperties}
    >
      <NavBar className="shrink-0" variant={theme} />

      {noWrapper && isTestEnvWarningVisible && (
        <motion.div
          initial={false}
          animate={{ y: navRevealState === 'hidden' ? 0 : NAV_BAR_HEIGHT_PX }}
          transition={shouldReduceMotion ? { duration: 0 } : NAV_BAR_TRANSITION}
          className="pointer-events-none fixed inset-x-0 top-0 z-40"
        >
          <TestEnvWarning className="pointer-events-auto" onHeightChange={setTestWarningHeight} />
        </motion.div>
      )}

      {!noWrapper && <TestEnvWarning className="shrink-0 mt-20" />}

      <div
        className={cn(
          'relative flex flex-col grow gap-y-10 overflow-x-hidden',
          !noWrapper && (isTestEnvWarningVisible ? 'pt-10 sm:pt-12' : 'pt-24 sm:pt-28'),
        )}
        id={PAGE_CONTAINER_ID}
        onScroll={handleScroll}
      >
        <Header />

        <WrapperComp className={cn('relative w-full shrink-0 grow')}>{contentDom}</WrapperComp>

        <Footer ref={footerRef} variant={theme} expanded={noWrapper} />
      </div>
    </div>
  );
};
